import { Directive, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl, SafeScript, SafeStyle, SafeUrl } from '@angular/platform-browser';
import { translate } from '@ngneat/transloco';
import { SubscriptionDurationUnit } from 'app/modules/user/payments/payments.types';
import { Store } from 'app/modules/user/releases/releases.types';
import { UtilService } from 'app/shared/util.service';
import { debounceTime, Subject, Subscription } from 'rxjs';

/**
 * Sanitize HTML
 */
@Pipe({
    name: 'safe'
})
export class SafePipe implements PipeTransform {
    // tslint:disable-next-line
    constructor(protected _sanitizer: DomSanitizer) {
    }

    /**
     * Transform
     *
     * @param value: string
     * @param type: string
     */
    transform(value: string, type: string): SafeHtml | SafeStyle | SafeScript | SafeUrl | SafeResourceUrl {
        switch (type) {
            case 'html':
                return this._sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this._sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this._sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this._sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this._sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                return this._sanitizer.bypassSecurityTrustHtml(value);
        }
    }
}

@Pipe({
    name: 'subscriptionDuration'
})
export class SubscriptionDurationPipe implements PipeTransform {

    transform(seconds: number, unit: SubscriptionDurationUnit): string {
        let conversionFactor = 60 * 60 * 24; //day is the lowest unit
        if (unit === 'years') {
            conversionFactor *= 365;
        } else if (unit === 'weeks') {
            conversionFactor *= 7;
        }

        let convertedDuration = seconds / conversionFactor;
        let unitString: string = unit;
        if (convertedDuration === 1) {
            unitString = unit.slice(0, -1);
        }

        return `${convertedDuration} ${translate(unitString)}`;
    }
}

@Pipe({
    name: 'cc'
})
export class CountryCodePipe implements PipeTransform {
    constructor(protected _utilService: UtilService) {
    }

    transform(cc: string): string {
       return this._utilService.getCountryNameFromAlpha2(cc);
    }
}

@Pipe({
    name: 'duration'
})
export class DurationPipe implements PipeTransform {

    transform(seconds: number): string {
        const hours: number = Math.floor(seconds / 60 / 60);
        let minutes: number = Math.floor(seconds / 60) - (hours * 60);
        seconds = Math.round(seconds - (hours * 60 * 60) - (minutes * 60));

        if (seconds === 60) {
            seconds = 0;
            minutes += 1;
        }

        if (hours > 0) {
            return hours + ':' + minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');
        }
        return minutes + ':' + seconds.toString().padStart(2, '0');
    }
}


@Directive({
    selector: '[autofocus]'
})
export class AutofocusDirective {
    constructor(private host: ElementRef) {
    }

    ngAfterViewInit() {
        this.host.nativeElement.focus();
    }
}

@Directive({
    selector: '[appDebounceClick]'
})
export class DebounceClickDirective implements OnInit, OnDestroy {
    @Input()
    debounceTime = 500;

    @Output()
    debounceClick = new EventEmitter();

    ignoreClicks = false;

    private clicks = new Subject();
    private subscription: Subscription;

    constructor() {
    }

    ngOnInit() {
        this.subscription = this.clicks.pipe(
            debounceTime(this.debounceTime)
        ).subscribe(e => this.ignoreClicks = false);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    @HostListener('click', ['$event'])
    clickEvent(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.ignoreClicks) {
            return;
        }
        this.debounceClick.emit(event)
        this.clicks.next(event);
        this.ignoreClicks = true;
    }
}

@Pipe({
    name: 'storeType'
})
export class StoresPipe implements PipeTransform {
    transform(stores: Store[], type: string): any {
        //return stores.filter(store => store.type.includes(type));
        return true; //TODO: Implement this if needed, else remove
    }
}
